import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { beginSso, listSsoConnections, login } from "../lib/api";
import { ApiError } from "../lib/api";
import { setSession } from "../lib/auth";
import { orgFromHost } from "../lib/format";
import { Alert, Spinner } from "../components/States";
import { Logo } from "../components/Logo";
import type { SsoConnection } from "../lib/types";

interface LocationState {
  from?: string;
}

// Where we remember the last-used email (opt-in, per-device, never the password).
const REMEMBER_KEY = "ac:remembered-username";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // No deep-link target → send to "/", which resolves the tenant's configured
  // default landing page (Settings → General; falls back to Tickets).
  const from = (location.state as LocationState | null)?.from ?? "/";
  const org = orgFromHost();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill the email if it was remembered on this device (opt-in).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setEmail(saved);
        setRemember(true);
      }
    } catch {
      /* localStorage unavailable (e.g. private mode) — just skip pre-fill */
    }
  }, []);

  // SSO connections are public; fetch directly (not via useApi, which would
  // navigate on a 401 — irrelevant here). A failed/empty load just hides SSO.
  const [connections, setConnections] = useState<SsoConnection[]>([]);
  const [ssoPending, setSsoPending] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    listSsoConnections(ctrl.signal)
      .then((res) => {
        // Tolerate a response that omits `connections` (e.g. a worker health/info
        // root that 200s without the expected body) — never let state go undefined.
        if (!ctrl.signal.aborted) setConnections(res?.connections ?? []);
      })
      .catch(() => {
        /* no SSO configured / not reachable — fall back to password only */
      });
    return () => ctrl.abort();
  }, []);

  async function startSso(conn: SsoConnection) {
    setSsoPending(conn.id);
    setError(null);
    try {
      const { authorizationUrl } = await beginSso(conn.id);
      // Hand the browser off to the IdP; the callback returns to /sso-complete.
      window.location.assign(authorizationUrl);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : `Could not start sign-in with ${conn.display_name}.`,
      );
      setSsoPending(null);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const res = await login(email.trim(), password);
      // Remember (or forget) the email per the checkbox — never the password.
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, email.trim());
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {
        /* localStorage unavailable — non-fatal, sign-in still proceeds */
      }
      setSession({ token: res.token, user: res.user });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Incorrect email or password.");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Could not reach the server. Check your connection and try again.");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="logo">
          <Logo size={26} /> {org}
        </div>
        <p className="lead">Sign in to your ActionConnect workspace.</p>

        {error ? <Alert kind="error">{error}</Alert> : null}

        <label className="field">
          <span className="label">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            placeholder="you@company.com"
            required
          />
        </label>

        <label className="field">
          <span className="label">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="••••••••"
            required
          />
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: "0.9rem",
            cursor: "pointer",
            margin: "-2px 0 10px",
          }}
        >
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          Remember my email on this device
        </label>

        <button className="btn primary" type="submit" disabled={pending} style={{ width: "100%" }}>
          {pending ? <Spinner label="Signing in…" /> : "Sign in"}
        </button>

        {connections.length > 0 ? (
          <>
            <div className="sso-divider" aria-hidden="true">
              <span>or</span>
            </div>
            <div className="sso-buttons">
              {connections.map((conn) => (
                <button
                  key={conn.id}
                  className="btn"
                  type="button"
                  style={{ width: "100%" }}
                  disabled={ssoPending !== null}
                  onClick={() => startSso(conn)}
                >
                  {ssoPending === conn.id ? (
                    <Spinner label={`Redirecting to ${conn.display_name}…`} />
                  ) : (
                    `Sign in with ${conn.display_name}`
                  )}
                </button>
              ))}
            </div>
          </>
        ) : null}
      </form>
    </div>
  );
}
